import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShoppingCart, Trash2, ArrowUpRight, Loader2, Package, Sparkles, GraduationCap, Wrench } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { useCart } from "../context/CartContext";
import { FORMATIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND_LABEL = { tool: "Outil", formation: "Formation", pack: "Pack" };
const KIND_ICON = { tool: Wrench, formation: GraduationCap, pack: Package };

export default function Panier() {
  const { items, remove, replaceWith, total } = useCart();
  const [packs, setPacks] = useState([]);
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/packs`).then(({ data }) => setPacks(data || [])).catch(() => {});
    axios.get(`${API}/tools`).then(({ data }) => setTools(data || [])).catch(() => {});
  }, []);

  const catalog = useMemo(() => {
    const map = {};
    FORMATIONS.forEach((f) => { map[f.lookup] = { name: f.title, price: f.price }; });
    tools.forEach((t) => { map[t.lookup_key] = { name: t.name, price: t.price }; });
    return map;
  }, [tools]);

  const cartKeys = items.map((i) => i.key);
  const suggestions = useMemo(() => packs.filter((p) =>
    !cartKeys.includes(p.lookup_key) &&
    p.items.length >= 2 &&
    p.items.every((lk) => cartKeys.includes(lk))
  ).map((p) => {
    const separate = p.items.reduce((s, lk) => s + (catalog[lk]?.price || 0), 0);
    return { ...p, separate, saving: Math.max(0, separate - p.price) };
  }), [packs, cartKeys.join(","), catalog]);

  const choosePack = (p) => {
    replaceWith(p.items, { key: p.lookup_key, kind: "pack", name: p.name, price: p.price, slug: p.slug });
    toast.success(`Pack « ${p.name} » appliqué.`, { description: `Vous économisez ${p.saving} €.` });
  };

  const checkout = async () => {
    if (!items.length) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_keys: cartKeys, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible d'ouvrir le paiement.", { description: e?.response?.data?.detail || undefined });
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="[ Panier ]"
        titleLines={["Votre panier."]}
        intro="Outils digitaux et formations réunis en une seule commande. Paiement sécurisé par Stripe, sans compte requis."
      />

      <section className="bg-paper pb-28" data-testid="cart-page">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          {items.length === 0 ? (
            <Reveal>
              <div className="rounded-sm border hairline bg-white p-10 md:p-14 text-center">
                <ShoppingCart size={44} className="text-ink/25 mx-auto" />
                <h2 className="font-display text-2xl md:text-3xl font-semibold mt-6">Votre panier est vide.</h2>
                <p className="text-ink/55 mt-3">Explorez nos outils digitaux et nos formations pour le remplir.</p>
                <div className="flex flex-wrap gap-3 justify-center mt-8">
                  <Link to="/outils-apps" data-testid="cart-browse-tools" className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-6 py-3.5 label-mono hover:bg-gold hover:text-ink transition-colors">Explorer les outils <ArrowUpRight size={14} /></Link>
                  <Link to="/formation" data-testid="cart-browse-formations" className="inline-flex items-center gap-2 rounded-full border hairline px-6 py-3.5 label-mono hover:bg-ink hover:text-paper transition-colors">Voir les formations</Link>
                </div>
              </div>
            </Reveal>
          ) : (
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-4">
                {suggestions.map((p) => (
                  <motion.div key={p.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid={`pack-suggestion-${p.slug}`}
                    className="rounded-sm border border-gold/50 bg-gold/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                    <Sparkles size={22} className="text-gold shrink-0" />
                    <div className="flex-1">
                      <p className="font-display font-semibold">Suggestion maline : {p.name}</p>
                      <p className="text-ink/60 text-sm mt-1">
                        Ces articles réunis dans le pack : {p.price} € au lieu de {p.separate} €
                        {p.saving > 0 && <span className="text-gold font-semibold"> — économisez {p.saving} €</span>}
                      </p>
                    </div>
                    <button onClick={() => choosePack(p)} data-testid={`choose-pack-${p.slug}`}
                      className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-5 py-2.5 label-mono hover:bg-ink hover:text-paper transition-colors shrink-0">
                      Choisir le pack
                    </button>
                  </motion.div>
                ))}

                {items.map((i) => {
                  const Icon = KIND_ICON[i.kind] || Package;
                  return (
                    <div key={i.key} data-testid={`cart-item-${i.key}`}
                      className="rounded-sm border hairline bg-white p-5 flex items-center gap-4">
                      <span className="w-11 h-11 rounded-sm bg-ink-900 text-gold flex items-center justify-center shrink-0"><Icon size={18} /></span>
                      <div className="flex-1 min-w-0">
                        <p className="label-mono text-ink/40">{KIND_LABEL[i.kind] || "Article"}</p>
                        <h3 className="font-display text-lg font-semibold leading-tight truncate">{i.name}</h3>
                      </div>
                      <span className="font-display text-lg font-semibold text-ink whitespace-nowrap">{i.price} €</span>
                      <button onClick={() => remove(i.key)} data-testid={`cart-remove-${i.key}`} aria-label="Retirer"
                        className="w-9 h-9 rounded-full border hairline flex items-center justify-center text-ink/50 hover:text-red-500 hover:border-red-300 transition-colors shrink-0">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-sm border hairline bg-white p-7 sticky top-28" data-testid="cart-summary">
                  <span className="label-mono text-ink/50">Récapitulatif</span>
                  <div className="flex items-baseline justify-between mt-5 pt-5 border-t hairline">
                    <span className="text-ink/60">{items.length} article{items.length > 1 ? "s" : ""}</span>
                    <span className="font-display text-4xl font-semibold text-gold" data-testid="cart-total">{total} €</span>
                  </div>
                  <button onClick={checkout} disabled={busy} data-testid="cart-checkout"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors mt-6 disabled:opacity-60">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <>Commander <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" /></>}
                  </button>
                  <p className="text-ink/45 text-xs mt-4 text-center">Paiement sécurisé Stripe. Aucun compte requis — vos outils sont disponibles immédiatement après paiement.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
